/**
 * ShipmentsPage - Logística > Remessas (Fase 2).
 *
 * Listagem server-side (get_shipments_paginated) com indicadores por situação,
 * filtros rápidos (busca, situação, transportadora, atrasadas), criação e detalhes.
 *
 * RELEASE: 2026-10-16 v1.5.0 - Card Total sem anel de seleção (só fundo) e Rascunho em slate (cor "Pendentes" de Tarefas).
 * RELEASE: 2026-10-16 v1.4.0 - Cards Total Remessa (sem canceladas) e Rascunho; Em preparação sem rascunho.
 * RELEASE: 2026-10-16 v1.3.0 - Alternador Lista/Kanban.
 * RELEASE: 2026-10-16 v1.2.0 - Busca na linha do título, sem subtítulo, filtros pílula e tabela fullBleed.
 * RELEASE: 2026-10-16 v1.1.0 - Tabela oficial ShipmentsTable (padrão O.S.), ordenação, filtro Envio/Retorno e filtros pílula.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Boxes, CheckCircle2, FilePen, LayoutGrid, List, PackageOpen, Plus, Search, Send } from 'lucide-react';
import {
  ListPageLayout, ListPageHeader, ListPageKpis, KpiCard, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useShipmentsList, type ShipmentsFilters } from '@/hooks/useShipments';
import { ShipmentFormDialog } from '@/components/logistics/ShipmentFormDialog';
import { ShipmentDetailsModal } from '@/components/logistics/ShipmentDetailsModal';
import { ShipmentsTable } from '@/components/logistics/ShipmentsTable';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import { SHIPMENT_STATUS_META, SHIPMENT_STATUS_ORDER, totalActiveShipments, type ShipmentSortField, type ShipmentStatus } from '@/components/logistics/shipmentStatus';


export default function ShipmentsPage() {
  const { canCreate, isAdmin } = usePermissionsContext();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<ShipmentsFilters>({ search: '', status: null, carrierId: null, overdueOnly: false, page: 1, pageSize: 25 });
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, isFetching } = useShipmentsList(filters);

  // Busca com atraso curto para não consultar a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const counts = data?.counts ?? {};
  const sum = (keys: ShipmentStatus[]) => keys.reduce((n, k) => n + (counts[k] ?? 0), 0);
  const toggleStatus = (s: ShipmentStatus) => setFilters((f) => ({ ...f, status: f.status === s ? null : s, overdueOnly: false, page: 1 }));
  const items = data?.items ?? [];

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Remessas"
        /* v1.2.0: busca na linha do título (padrão listagem de O.S.); sem subtítulo */
        center={
          <div className="relative w-full max-w-md mx-4 hidden md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input placeholder="Buscar por número, rastreio ou destinatário..." value={searchInput} maxLength={100}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50" />
          </div>
        }
        actions={(
          <div className="flex items-center gap-2">
            {/* v1.3.0: alternador Lista/Kanban (padrão Propostas) */}
            <div className="flex items-center rounded-md border p-0.5">
              <Button variant="secondary" size="icon-sm" title="Visualizar em lista"><List className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon-sm" title="Visualizar em kanban por situação" onClick={() => navigate('/tenant/logistics/shipments/kanban')}><LayoutGrid className="h-3.5 w-3.5" /></Button>
            </div>
            {(isAdmin || canCreate('log_shipments')) && (
              <Button size="xs" onClick={() => setCreating(true)}><Plus className="mr-1.5 h-4 w-4" />Nova remessa</Button>
            )}
          </div>
        )}
      />

      {/* v1.4.0: 6 cards — Total (sem canceladas) e Rascunho separados de Em preparação */}
      <ListPageKpis columns={6}>
        {/* v1.5.0: Total sem borda de seleção (destaque só no fundo); Rascunho em slate,
            mesma paleta do card "Pendentes" da listagem de Tarefas */}
        <KpiCard color="cyan" icon={Boxes} label="Total Remessa" value={totalActiveShipments(counts)}
          active={!filters.status && !filters.overdueOnly}
          activeClassName="bg-cyan-200/70 dark:bg-cyan-900/50"
          onClick={() => setFilters((f) => ({ ...f, status: null, overdueOnly: false, page: 1 }))} />
        <KpiCard color="slate" icon={FilePen} label="Rascunho" value={sum(['draft'])} active={filters.status === 'draft'} onClick={() => toggleStatus('draft')} />
        <KpiCard color="purple" icon={PackageOpen} label="Em preparação" value={sum(['preparing', 'ready'])} />
        <KpiCard color="blue" icon={Send} label="Em trânsito" value={sum(['shipped', 'in_transit'])} />
        <KpiCard color="green" icon={CheckCircle2} label="Entregues" value={sum(['delivered'])} active={filters.status === 'delivered'} onClick={() => toggleStatus('delivered')} />
        <KpiCard color="red" icon={AlertTriangle} label="Atrasadas" value={data?.overdue ?? 0} active={filters.overdueOnly}
          onClick={() => setFilters((f) => ({ ...f, overdueOnly: !f.overdueOnly, status: null, page: 1 }))} />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={isFetching}>
        <Input className={`${LIST_FILTERS_INPUT_CLASS} min-w-0 flex-1 md:hidden`} value={searchInput} maxLength={100}
          placeholder="Buscar por número, rastreio ou destinatário..." onChange={(e) => setSearchInput(e.target.value)} />
        <Select value={filters.status ?? 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? null : (v as ShipmentStatus), page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} w-auto shrink-0 sm:w-[170px]`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as situações</SelectItem>
            {SHIPMENT_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{SHIPMENT_STATUS_META[s].label} ({counts[s] ?? 0})</SelectItem>)}
          </SelectContent>
        </Select>
        {/* v1.1.0: Envio / Retorno */}
        <Select value={filters.direction ?? 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, direction: v === 'all' ? null : (v as 'outbound' | 'return'), page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} w-auto shrink-0 sm:w-[130px]`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="outbound">Envio</SelectItem>
            <SelectItem value="return">Retorno</SelectItem>
          </SelectContent>
        </Select>
        <PersonSelector role="carrier" quickFilter value={filters.carrierId} placeholder="Transportadora" noneLabel="Todas as transportadoras"
          className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} w-auto shrink-0`} onSelect={(id) => setFilters((f) => ({ ...f, carrierId: id, page: 1 }))} />
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        <div className="w-full overflow-x-auto">
          <ShipmentsTable items={items} isLoading={isLoading} onOpen={setOpenId}
            sortField={(filters.sortField as ShipmentSortField) ?? 'created_at'} sortDirection={filters.sortDirection ?? 'desc'}
            onSort={(field) => setFilters((f) => ({ ...f, sortField: field, page: 1,
              sortDirection: (f.sortField ?? 'created_at') === field && (f.sortDirection ?? 'desc') === 'desc' ? 'asc' : 'desc' }))} />
        </div>
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))} pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>

      <ShipmentFormDialog open={creating} onOpenChange={setCreating} shipment={null} onSaved={(id) => setOpenId(id)} />
      <ShipmentDetailsModal shipmentId={openId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} onOpenShipment={setOpenId} />
    </ListPageLayout>
  );
}
